import { Injectable } from '@angular/core';
import { Observable, shareReplay, switchMap } from 'rxjs';
import { FacilityReservation, PaymentStatus } from '@models';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class ReservationService {
  /** All reservations ordered by date desc */
  public all$: Observable<FacilityReservation[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
    private readonly functions: Functions,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilityReservation>(
          this.tenantContext.collections.FACILITY_RESERVATIONS,
          orderBy('date', 'desc'),
        ),
      ),
      shareReplay(1),
    );
  }

  /** Reservations for a specific facility */
  byFacility$(facilityId: string): Observable<FacilityReservation[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilityReservation>(
          this.tenantContext.collections.FACILITY_RESERVATIONS,
          where('facilityId', '==', facilityId),
          orderBy('date', 'desc'),
        ),
      ),
    );
  }

  /** Reservations for a specific date (ISO 'YYYY-MM-DD') */
  byDate$(date: string): Observable<FacilityReservation[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilityReservation>(
          this.tenantContext.collections.FACILITY_RESERVATIONS,
          where('date', '==', date),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /** Reservations by the current user */
  byUser$(userId: string): Observable<FacilityReservation[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilityReservation>(
          this.tenantContext.collections.FACILITY_RESERVATIONS,
          where('userId', '==', userId),
          orderBy('date', 'desc'),
        ),
      ),
    );
  }

  async create(data: Omit<FacilityReservation, 'id'>): Promise<string> {
    return this.firestore.create<FacilityReservation>(
      this.tenantContext.collections.FACILITY_RESERVATIONS,
      data,
    );
  }

  async updateStatus(
    id: string,
    status: FacilityReservation['status'],
  ): Promise<void> {
    return this.firestore.update<FacilityReservation>(
      this.tenantContext.collections.FACILITY_RESERVATIONS,
      { id, status },
    );
  }

  /** Reservations for a specific facility on a specific date */
  byFacilityAndDate$(
    facilityId: string,
    date: string,
  ): Observable<FacilityReservation[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilityReservation>(
          this.tenantContext.collections.FACILITY_RESERVATIONS,
          where('facilityId', '==', facilityId),
          where('date', '==', date),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  /** All reservations across all facilities in a date range */
  byDateRange$(
    dateFrom: string,
    dateTo: string,
  ): Observable<FacilityReservation[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<FacilityReservation>(
          this.tenantContext.collections.FACILITY_RESERVATIONS,
          where('date', '>=', dateFrom),
          where('date', '<=', dateTo),
          orderBy('date', 'asc'),
          orderBy('startTime', 'asc'),
        ),
      ),
    );
  }

  async updatePaymentStatus(
    id: string,
    paymentStatus: PaymentStatus,
  ): Promise<void> {
    return this.firestore.update<FacilityReservation>(
      this.tenantContext.collections.FACILITY_RESERVATIONS,
      { id, paymentStatus },
    );
  }

  async update(
    data: Partial<FacilityReservation> & { id: string },
  ): Promise<void> {
    return this.firestore.update<FacilityReservation>(
      this.tenantContext.collections.FACILITY_RESERVATIONS,
      data,
    );
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.FACILITY_RESERVATIONS,
      id,
    );
  }

  /**
   * Calls the `cancelSlotReservation` Cloud Function which:
   * - sets reservation status → CANCELLED
   * - decrements slot.bookedCount (re-opens slot if it was FULL)
   * - notifies all users on the slot's waitlist
   */
  async cancelSlotReservation(
    reservationId: string,
    refundBono?: boolean,
  ): Promise<void> {
    const fn = httpsCallable<
      { reservationId: string; refundBono?: boolean },
      { success: boolean }
    >(this.functions, 'cancelSlotReservation');
    await fn({ reservationId, refundBono });
  }

  /**
   * Calls the `cancelSlot` Cloud Function (admin only) which cancels a whole
   * slot with cascade: marks the slot CANCELLED, cancels every active
   * reservation (refunding the bono session when applicable) and notifies both
   * the reserved users and the waitlist. Idempotent if already cancelled.
   */
  async cancelSlot(slotId: string): Promise<void> {
    const fn = httpsCallable<
      { slotId: string },
      {
        success: boolean;
        alreadyCancelled?: boolean;
        reservationsCancelled?: number;
        notified?: number;
      }
    >(this.functions, 'cancelSlot');
    await fn({ slotId });
  }

  /**
   * Calls the `cancelSlotSeries` Cloud Function (admin only) which cancels every
   * non-cancelled slot in a series, applying the same cascade as `cancelSlot`
   * to each (cancel reservations, refund bonos, notify reserved + waitlist).
   */
  async cancelSlotSeries(seriesId: string): Promise<void> {
    const fn = httpsCallable<
      { seriesId: string },
      {
        success: boolean;
        slotsCancelled?: number;
        reservationsCancelled?: number;
        notified?: number;
      }
    >(this.functions, 'cancelSlotSeries');
    await fn({ seriesId });
  }

  /**
   * Calls the `promoteWaitlistEntry` Cloud Function (admin only) which turns a
   * waitlist entry into a confirmed reservation:
   * - creates the reservation (status CONFIRMED) from the slot + waitlist data
   * - removes the entry from the slot's waitlist
   * - increments slot.bookedCount (marks the slot FULL when it fills up)
   */
  async promoteWaitlistEntry(slotId: string, userId: string): Promise<string> {
    const fn = httpsCallable<
      { slotId: string; userId: string },
      { success: boolean; reservationId: string }
    >(this.functions, 'promoteWaitlistEntry');
    const res = await fn({ slotId, userId });
    return res.data.reservationId;
  }

  /**
   * Calls the `createSlotReservation` Cloud Function which atomically:
   * - creates the reservation (status CONFIRMED)
   * - increments slot.bookedCount (marks the slot FULL when it fills up)
   * - burns one session from the member bono when paid with a bono
   *
   * All validation happens server-side via the Admin SDK, so the client never
   * writes `member_bonos` / `facility_slots` directly (which rules deny).
   */
  async createSlotReservation(payload: {
    slotId: string;
    memberBonoId?: string;
    notes?: string;
  }): Promise<string> {
    const fn = httpsCallable<
      { slotId: string; memberBonoId?: string; notes?: string },
      { success: boolean; reservationId: string }
    >(this.functions, 'createSlotReservation');
    const res = await fn(payload);
    return res.data.reservationId;
  }

  /**
   * Calls the `joinSlotWaitlist` Cloud Function which adds the current member to
   * a full slot's waitlist server-side (members cannot write `facility_slots`
   * directly — Firestore rules deny it). Idempotent if already on the waitlist.
   */
  async joinSlotWaitlist(slotId: string): Promise<void> {
    const fn = httpsCallable<{ slotId: string }, { success: boolean }>(
      this.functions,
      'joinSlotWaitlist',
    );
    await fn({ slotId });
  }

  /**
   * Calls the `leaveSlotWaitlist` Cloud Function which removes the current
   * member from a slot's waitlist server-side. Idempotent if not on the list.
   */
  async leaveSlotWaitlist(slotId: string): Promise<void> {
    const fn = httpsCallable<{ slotId: string }, { success: boolean }>(
      this.functions,
      'leaveSlotWaitlist',
    );
    await fn({ slotId });
  }
}
